import { View, Text, Platform, Pressable } from "react-native";
import { Link } from "expo-router";
import { Ionicons } from "@expo/vector-icons";
import { useEffect } from "react";
import {
  GuideSection,
  GuideCard,
  GuideStep,
  GuideTip,
  GuideGameCard,
  PrintButton,
} from "./_components";

/** Section divider for print page breaks */
const SectionBreak = () => <View className="h-8" style={{ pageBreakBefore: "always" } as any} />;

/**
 * Full guide page combining all content for PDF export.
 * Injects print-specific CSS for clean PDF generation.
 */
export default function FullGuidePage() {
  if (Platform.OS !== "web") return null;

  useEffect(() => {
    if (typeof document === "undefined") return;

    const style = document.createElement("style");
    style.id = "guide-full-styles";
    style.textContent = `
      /* Force document-level scrolling for the full guide page.
         React-native-web sets overflow:hidden + flex height constraints
         on every View div, trapping content. Override the entire chain. */
      html, body, #root {
        height: auto !important;
        overflow: auto !important;
      }
      #root > div, #root > div > div, #root > div > div > div,
      #root > div > div > div > div, #root > div > div > div > div > div {
        overflow: visible !important;
        height: auto !important;
        flex-basis: auto !important;
      }
      @media print {
        body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        [data-print-hide] { display: none !important; }
        [data-print-break] { break-before: page; }
        * { break-inside: avoid-page; }
        .guide-section { break-inside: avoid; }
      }
    `;
    document.head.appendChild(style);

    return () => { style.remove(); };
  }, []);

  const handlePrint = () => {
    if (typeof window !== "undefined") window.print();
  };

  return (
    <View nativeID="full-guide-root" className="bg-white" style={{ minHeight: "100vh" } as any}>
      {/* Top bar (hidden in print) */}
      <View
        className="bg-white border-b border-gray-200 px-6 py-4 flex-row items-center justify-between"
        {...{ "data-print-hide": true } as any}
      >
        <Link href="/(web)/guide" asChild>
          <Pressable className="flex-row items-center gap-2" style={{ cursor: "pointer" } as any}>
            <Ionicons name="arrow-back" size={20} color="#7C3AED" />
            <Text className="text-sm font-medium text-violet-700">Terug naar handleiding</Text>
          </Pressable>
        </Link>
        <PrintButton />
      </View>

      {/* Content */}
      <View className="max-w-3xl mx-auto px-6 py-10">
        {/* Title page */}
        <View className="items-center mb-16">
          <View className="w-20 h-20 bg-violet-100 rounded-3xl items-center justify-center mb-6">
            <Text className="text-4xl">👨‍👩‍👧‍👦</Text>
          </View>
          <Text className="text-4xl font-bold text-gray-900 text-center">
            Staatsen Familie App
          </Text>
          <Text className="text-xl text-gray-500 text-center mt-3">
            Complete Handleiding
          </Text>
          <View className="w-16 h-1 bg-violet-500 rounded-full mt-6" />
        </View>

        {/* Table of contents */}
        <View className="bg-gray-50 rounded-2xl p-6 mb-12">
          <Text className="text-lg font-bold text-gray-900 mb-4">Inhoudsopgave</Text>
          {[
            "1. Aan de Slag",
            "2. Familie Weekend",
            "3. Rollen",
            "4. Teams",
            "5. Agenda",
            "6. Chat",
            "7. Spellen",
            "8. Uitdagingen",
            "9. Punten",
            "10. Speurtocht",
            "11. Meldingen",
            "12. Foto's",
          ].map((item) => (
            <Text key={item} className="text-sm text-gray-600 py-1">{item}</Text>
          ))}
        </View>

        {/* ============================================================ */}
        {/* 1. AAN DE SLAG */}
        {/* ============================================================ */}
        <GuideSection icon="rocket" title="1. Aan de Slag" color="#7C3AED"
          intro="Download de app, maak je account aan en ontdek het startscherm."
        >
          <Text className="text-base font-semibold text-gray-900 mb-3">Account aanmaken</Text>
          <Text className="text-sm text-gray-600 leading-5 mb-4">
            De Staatsen Familie App is exclusief voor familieleden. Je hebt een uitnodiging nodig.
          </Text>
          <GuideStep step={1} title="Download de app" description="Zoek 'Staatsen' in de App Store of Google Play Store." />
          <GuideStep step={2} title="Account aanmaken" description="Kies 'Account aanmaken' en vul je e-mailadres, naam en wachtwoord in." />
          <GuideStep step={3} title="Profiel voltooien" description="Voeg een profielfoto toe en vul je bijnaam in." />
          <GuideTip title="Kinderen">
            Jonge kinderen kunnen via een "afhankelijk profiel" meedoen. Een ouder beheert hun account en kan wisselen om namens het kind te spelen.
          </GuideTip>
        </GuideSection>

        <SectionBreak />

        {/* ============================================================ */}
        {/* 2. FAMILIE WEEKEND */}
        {/* ============================================================ */}
        <GuideSection icon="calendar" title="2. Familie Weekend" color="#3B82F6"
          intro="Het hart van de app: samen spelen, punten verdienen en herinneringen maken."
        >
          <Text className="text-sm text-gray-600 leading-5 mb-4">
            Het familie weekend is een speciaal evenement met teams, spellen en punten. De cyclus is tweejaarlijks en omvat zowel de Familie Dag als het hoofdweekend.
          </Text>
          <GuideCard icon="trophy" title="Competitie Modus" description="Tijdens een actief weekend verdien je punten voor je team. De commissie stelt per spel de puntenverdeling in." color="#F59E0B" />
          <GuideCard icon="game-controller" title="Vrij Spelen" description="Buiten het weekend kun je alle spellen spelen zonder puntenstress. Scores worden apart bijgehouden." color="#3B82F6" />
          <GuideCard icon="sunny" title="Familie Dag" description="Een korter evenement met een eigen locatie en programma, binnen dezelfde cyclus." color="#EC4899" />
          <GuideCard icon="eye-off" title="Locatie Onthulling" description="De commissie kan de locatie geheim houden en later spectaculair onthullen met een countdown of animatie." color="#8B5CF6" />
        </GuideSection>

        <SectionBreak />

        {/* ============================================================ */}
        {/* 3. ROLLEN */}
        {/* ============================================================ */}
        <GuideSection icon="shield" title="3. Rollen" color="#D97706"
          intro="Er zijn drie rollen met verschillende rechten."
        >
          <GuideCard icon="person" title="Familielid" description="Chatten, spellen spelen, foto's uploaden, uitdagingen voltooien, agenda bekijken en punten verdienen voor je team." color="#3B82F6" />
          <GuideCard icon="shield" title="Familie Admin (Commissie)" description="Alles van Familielid plus: teams beheren, spellen configureren, agenda beheren, uitdagingen aanmaken, punten toekennen, live quizzes hosten en speurtochten opzetten." color="#D97706" />
          <GuideCard icon="star" title="Super Admin" description="Alles van Admin plus: weekenden aanmaken, gebruikers beheren, rollen toewijzen, afhankelijke profielen aanmaken en systeem-brede instellingen aanpassen." color="#7C3AED" />
          <GuideTip icon="swap-horizontal" title="Voogden">
            Een voogd kan wisselen naar het profiel van hun kind om namens het kind deel te nemen aan alle activiteiten.
          </GuideTip>
        </GuideSection>

        <SectionBreak />

        {/* ============================================================ */}
        {/* 4. TEAMS */}
        {/* ============================================================ */}
        <GuideSection icon="people" title="4. Teams" color="#EC4899"
          intro="Tijdens het weekend word je ingedeeld in een team."
        >
          <Text className="text-sm text-gray-600 leading-5 mb-4">
            Teams worden door de commissie samengesteld. Elk team heeft een naam, kleur, captain en eigen groepschat. Teams werken samen om de meeste punten te verdienen.
          </Text>
          <GuideCard icon="color-palette" title="Aanpassen" description="Personaliseer je team met een eigen naam, slogan, kleur en avatar via de Avatar Studio." color="#EC4899" />
          <GuideCard icon="trophy" title="Teamstand" description="De teamstand toont alle teams gerangschikt op totaalscore. Bekijk per team de score-uitsplitsing en individuele bijdragen." color="#F59E0B" />
          <GuideCard icon="chatbubbles" title="Team Chat" description="Elk team heeft automatisch een eigen groepschat voor strategie en samenwerking." color="#6366F1" />
        </GuideSection>

        <SectionBreak />

        {/* ============================================================ */}
        {/* 5. AGENDA */}
        {/* ============================================================ */}
        <GuideSection icon="today" title="5. Agenda" color="#10B981"
          intro="Het programma van het weekend en je eigen evenementen."
        >
          <Text className="text-sm text-gray-600 leading-5 mb-4">
            De agenda toont alle evenementen per dag. Evenementen zijn kleurgecodeerd per categorie: activiteiten, maaltijden, spellen, ceremonies en vrije tijd.
          </Text>
          <GuideCard icon="add-circle" title="Eigen evenementen" description="Maak privé, team of publieke evenementen aan. Kies categorie, kleur, locatie en optioneel een animatie." color="#10B981" />
          <GuideCard icon="eye-off" title="Onthullingen" description="De commissie kan details verbergen en later onthullen via countdown, hints of uitdagingen." color="#8B5CF6" />
          <GuideCard icon="alarm" title="Herinneringen" description="Stel herinneringen in voor evenementen. Exporteer naar Google, Apple of andere agenda-apps." color="#F59E0B" />
        </GuideSection>

        <SectionBreak />

        {/* ============================================================ */}
        {/* 6. CHAT */}
        {/* ============================================================ */}
        <GuideSection icon="chatbubbles" title="6. Chat" color="#6366F1"
          intro="Blijf in contact via berichten, groepschats, polls en media."
        >
          <GuideCard icon="people" title="Familie Chat" description="Automatische groepschat met alle familieleden." color="#7C3AED" />
          <GuideCard icon="person" title="Privé Chat" description="Één-op-één gesprekken met familieleden." color="#3B82F6" />
          <GuideCard icon="chatbubbles" title="Groepschat" description="Maak eigen groepschats met familieleden naar keuze." color="#10B981" />
          <GuideCard icon="shield" title="Team Chat" description="Elke team heeft automatisch een eigen chat." color="#EC4899" />
          <Text className="text-base font-semibold text-gray-900 mt-4 mb-3">Berichten</Text>
          <Text className="text-sm text-gray-600 leading-5 mb-4">
            Verstuur tekst, foto's en bestanden. Swipe om te antwoorden. Maak polls aan voor groepsbeslissingen (2-10 opties, optioneel anoniem en met deadline).
          </Text>
        </GuideSection>

        <SectionBreak />

        {/* ============================================================ */}
        {/* 7. SPELLEN */}
        {/* ============================================================ */}
        <GuideSection icon="game-controller" title="7. Spellen" color="#EF4444"
          intro="9 verschillende spellen: solo, versus of als team."
        >
          <GuideGameCard icon="grid" name="Memory" description="Vind alle paren zo snel mogelijk." modes={["Solo", "Versus"]} color="#8B5CF6" howToPlay="Draai twee kaarten om. Bij een match blijven ze open. Vind alle paren met zo min mogelijk zetten." />
          <GuideGameCard icon="help-circle" name="Quiz" description="Meerkeuzevragen uit verschillende categorieën." modes={["Solo"]} color="#3B82F6" howToPlay="Kies het juiste antwoord binnen de tijdslimiet. Snelheid = meer punten." />
          <GuideGameCard icon="tv" name="Live Quiz" description="Kahoot-achtige teamquiz met quizmaster." modes={["Team"]} color="#EF4444" howToPlay="Teams kiezen een mascotte. De captain geeft de antwoorden op vragen van de quizmaster." />
          <GuideGameCard icon="map" name="Speurtocht" description="Scavenger hunt met locaties, QR-codes en puzzels." modes={["Team"]} color="#06B6D4" howToPlay="Volg de stappen: bezoek locaties, scan QR-codes, maak foto's en los puzzels op." />
          <GuideGameCard icon="apps" name="Dammen" description="Nederlands damspel op een 10x10 bord." modes={["Solo (vs AI)", "Versus"]} color="#10B981" howToPlay="Beweeg diagonaal, sla verplicht. Bereik de overkant om een dam te worden." />
          <GuideGameCard icon="keypad" name="Sudoku" description="Vul het 9x9 raster correct in." modes={["Solo"]} color="#3B82F6" howToPlay="Elke rij, kolom en 3x3 blok bevat de cijfers 1-9 precies één keer." />
          <GuideGameCard icon="diamond" name="Match 3" description="Wissel edelstenen voor rijen van 3+." modes={["Solo"]} color="#EC4899" howToPlay="Wissel naast elkaar liggende stenen. Grotere combo's = meer punten." />
          <GuideGameCard icon="walk" name="Endless Runner" description="Ren zo ver mogelijk als Opa of Oma!" modes={["Solo"]} color="#F59E0B" howToPlay="Tik om te springen. Verzamel munten. Hoe verder, hoe hoger je score." />
          <GuideGameCard icon="close" name="Boter Kaas en Eieren" description="Best-of-3 of best-of-5 serie." modes={["Solo (vs AI)", "Versus"]} color="#6366F1" howToPlay="Zet X of O op het 3x3 bord. Drie op een rij wint de ronde." />
        </GuideSection>

        <SectionBreak />

        {/* ============================================================ */}
        {/* 8. UITDAGINGEN */}
        {/* ============================================================ */}
        <GuideSection icon="flash" title="8. Uitdagingen" color="#F59E0B"
          intro="Voltooi uitdagingen en opdrachten voor bonuspunten."
        >
          <GuideCard icon="camera" title="Foto-opdracht" description="Maak een foto die aan de opdracht voldoet. De commissie beoordeelt je inzending." color="#8B5CF6" />
          <GuideCard icon="help-circle" title="Quiz-uitdaging" description="Beantwoord vragen correct over de familie of een ander onderwerp." color="#3B82F6" />
          <GuideCard icon="location" title="Locatie-uitdaging" description="Ga naar een specifieke locatie en bewijs dat je er bent." color="#10B981" />
          <GuideCard icon="checkbox" title="Taak-uitdaging" description="Voltooi een taak en stuur bewijs in ter beoordeling." color="#F59E0B" />
          <GuideCard icon="list" title="Meerdelige opdrachten" description="Grotere opdrachten met meerdere secties. Voltooide secties worden afgevinkt." color="#7C3AED" />
          <GuideTip variant="warning" icon="alarm" title="Deadlines">
            Let op de deadline! Uitdagingen met een korte deadline zijn vaak meer punten waard.
          </GuideTip>
        </GuideSection>

        <SectionBreak />

        {/* ============================================================ */}
        {/* 9. PUNTEN */}
        {/* ============================================================ */}
        <GuideSection icon="trophy" title="9. Punten" color="#F59E0B"
          intro="Verdien punten door spellen te winnen en uitdagingen te voltooien."
        >
          <GuideCard icon="game-controller" title="Spellen winnen" description="Elk spel heeft een eigen puntenwaarde, ingesteld door de commissie." color="#3B82F6" />
          <GuideCard icon="flash" title="Uitdagingen" description="Goedgekeurde inzendingen leveren punten op voor je team." color="#F59E0B" />
          <GuideCard icon="star" title="Bonuspunten" description="De commissie kan handmatig bonuspunten toekennen." color="#8B5CF6" />
          <GuideCard icon="ribbon" title="Deelname" description="Bij sommige spellen krijg je ook punten voor deelname." color="#10B981" />
          <Text className="text-sm text-gray-600 leading-5 mt-4 mb-4">
            Alle punten tellen mee voor de totaalscore van je team. Bekijk de teamstand, individuele bijdragen en scoregeschiedenis op het leaderboard.
          </Text>
        </GuideSection>

        <SectionBreak />

        {/* ============================================================ */}
        {/* 10. SPEURTOCHT */}
        {/* ============================================================ */}
        <GuideSection icon="map" title="10. Speurtocht" color="#06B6D4"
          intro="Een interactieve scavenger hunt door het weekendgebied."
        >
          <Text className="text-sm text-gray-600 leading-5 mb-4">
            Teams volgen een route met stappen. Elke stap is een uitdaging: een locatie bezoeken, QR-code scannen, foto maken, puzzel oplossen of woord raden.
          </Text>
          <GuideStep step={1} title="Start de speurtocht" description="Verschijnt op je startscherm wanneer de commissie deze activeert." color="#06B6D4" />
          <GuideStep step={2} title="Lees de instructies" description="Elke stap bevat uitleg over wat je moet doen." color="#06B6D4" />
          <GuideStep step={3} title="Voltooi de stap" description="De app controleert automatisch of je de stap hebt voltooid." color="#06B6D4" />
          <GuideStep step={4} title="Volgende stap" description="De volgende stap wordt ontgrendeld na voltooiing." color="#06B6D4" />
          <GuideTip title="GPS & Camera">
            Zorg dat locatieservices en camera-toegang aan staan op je telefoon voor locatie- en QR-stappen.
          </GuideTip>
        </GuideSection>

        <SectionBreak />

        {/* ============================================================ */}
        {/* 11. MELDINGEN */}
        {/* ============================================================ */}
        <GuideSection icon="notifications" title="11. Meldingen" color="#8B5CF6"
          intro="Blijf op de hoogte via push-meldingen en in-app notificaties."
        >
          <Text className="text-sm text-gray-600 leading-5 mb-4">
            Je ontvangt meldingen voor: nieuwe uitdagingen, beoordelingen, chatberichten, aankondigingen, evenement-herinneringen, score-updates en teamwijzigingen.
          </Text>
          <GuideCard icon="phone-portrait" title="Push-meldingen" description="Verschijnen op je telefoon, ook als de app niet open is." color="#3B82F6" />
          <GuideCard icon="notifications" title="In-app meldingen" description="Overzicht in de app, gegroepeerd op datum. Swipe om als gelezen te markeren." color="#8B5CF6" />
          <GuideCard icon="settings" title="Instellingen" description="Stel per type in of je meldingen wilt ontvangen via Profiel > Meldingen." color="#6B7280" />
        </GuideSection>

        <SectionBreak />

        {/* ============================================================ */}
        {/* 12. FOTO'S */}
        {/* ============================================================ */}
        <GuideSection icon="images" title="12. Foto's" color="#EC4899"
          intro="Deel herinneringen via de familie galerij."
        >
          <Text className="text-sm text-gray-600 leading-5 mb-4">
            Upload foto's via de galerij, chat of foto-opdrachten. Alle foto's komen samen in de familie galerij waar je kunt browsen, liken en reageren.
          </Text>
          <GuideCard icon="heart" title="Liken" description="Tik op het hartje om een foto te liken. Bekijk wie er heeft geliked." color="#EF4444" />
          <GuideCard icon="chatbubble" title="Reageren" description="Plaats reacties onder foto's om herinneringen te delen." color="#6366F1" />
          <GuideCard icon="download" title="Downloaden" description="Sla foto's op in je telefoon's fotobibliotheek." color="#10B981" />
        </GuideSection>

        {/* Footer */}
        <View className="mt-16 pt-8 border-t border-gray-200 items-center">
          <Text className="text-sm text-gray-400 text-center">
            Staatsen Familie App — Handleiding
          </Text>
          <Text className="text-xs text-gray-300 text-center mt-1">
            © {new Date().getFullYear()} — Gemaakt met ❤️ voor de familie
          </Text>
        </View>
      </View>
    </View>
  );
}
